import type { JsonRecord } from '@/domain/entities/property-schema';
import type { FlatField } from '../types';
import { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronRight, Trash2 } from 'lucide-react';
import { getAtPath, isAllowedForSystemType } from '../utils';
import { NodeEditor } from './NodeEditor';
import type { AttributeNode } from '@/types/schemas';

export function ArrayOfObjectsEditor(props: {
  field: FlatField;
  tplData: JsonRecord;
  setValueForPath: (path: string, value: unknown) => void;
  readOnly?: boolean;
}) {
  const { field, tplData, setValueForPath, readOnly = false } = props;
  const isSystemsArray = field.path === 'systems';

  const item = field.node.items;
  const isArrayOfObjects = item?.type === 'object' && (item.attributes?.length ?? 0) > 0;

  // collapse state per array index
  const [openByIndex, setOpenByIndex] = useState<Record<number, boolean>>({});

  const isOpen = (idx: number) => openByIndex[idx] ?? false;
  const toggleOpen = (idx: number) => setOpenByIndex((prev) => ({ ...prev, [idx]: !(prev[idx] ?? false) }));

  if (!isArrayOfObjects) {
    return (
      <div className="text-muted-foreground rounded-xl border p-4 text-sm">
        Only “array of objects” is supported right now.
      </div>
    );
  }

  const arrVal = getAtPath(tplData, field.path);
  const items = Array.isArray(arrVal) ? arrVal : [];

  const getItemLabel = (idx: number) => {
    const titlePath = `${field.path}.${idx}.title`;
    const title = getAtPath(tplData, titlePath);

    if (typeof title === 'string' && title.trim().length > 0) return title.trim();
    return `Item ${idx + 1}`;
  };

  const addItem = () => {
    const nextIdx = items.length;
    setValueForPath(field.path, [...items, {}]);
    // auto-expand the new item
    setOpenByIndex((prev) => ({ ...prev, [nextIdx]: true }));
  };

  const removeItem = (idx: number) => {
    setValueForPath(
      field.path,
      items.filter((_, i) => i !== idx),
    );

    // reindex collapse map to match new array indices
    setOpenByIndex((prev) => {
      const next: Record<number, boolean> = {};
      for (const [k, v] of Object.entries(prev)) {
        const i = Number(k);
        if (!Number.isFinite(i)) continue;
        if (i < idx) next[i] = v;
        if (i > idx) next[i - 1] = v;
      }
      return next;
    });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label>Items</Label>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              const next: Record<number, boolean> = {};
              items.forEach((_, i) => (next[i] = true));
              setOpenByIndex(next);
            }}
          >
            Expand all
          </Button>

          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              const next: Record<number, boolean> = {};
              items.forEach((_, i) => (next[i] = false));
              setOpenByIndex(next);
            }}
          >
            Collapse all
          </Button>

          {!readOnly ? (
            <Button type="button" variant="outline" size="sm" onClick={addItem}>
              Add item
            </Button>
          ) : null}
        </div>
      </div>

      {items.length === 0 ? (
        <div className="text-muted-foreground rounded-xl border p-4 text-sm">
          {readOnly ? 'No items yet.' : 'No items yet. Click “Add item”.'}
        </div>
      ) : null}

      {items.map((_, idx) => (
        <div key={idx} className="rounded-xl border">
          {/* header row */}
          <div className="flex items-center justify-between px-3 py-2">
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="hover:bg-muted rounded-md p-1"
                onClick={() => toggleOpen(idx)}
                aria-label={isOpen(idx) ? 'Collapse item' : 'Expand item'}
              >
                {isOpen(idx) ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </button>
              <div className="text-sm font-medium">
                {getItemLabel(idx)}
                <span className="text-muted-foreground ml-2 text-xs">#{idx + 1}</span>
              </div>
            </div>

            {!readOnly ? (
              <Button type="button" variant="ghost" size="sm" onClick={() => removeItem(idx)}>
                <Trash2 className="mr-2 h-4 w-4" />
                Remove
              </Button>
            ) : null}
          </div>

          {/* body */}
          {isOpen(idx) ? (
            <div className="space-y-3 px-3 pb-3">
              {(item.attributes ?? []).map((child) => {
                const childPath = `${field.path}.${idx}.${child.key}`;
                const childValue = getAtPath(tplData, childPath);

                // ✅ If this is systems[].configuration, filter its children by systems[].systemType
                if (isSystemsArray && child.key === 'configuration' && child.type === 'object') {
                  const systemTypePath = `${field.path}.${idx}.systemType`;
                  const systemType = getAtPath(tplData, systemTypePath);
                  const systemTypeStr = typeof systemType === 'string' ? systemType : undefined;

                  // child here is AttributeNode (UI node), but the metadata is on the SCHEMA.
                  // So you need access to the JsonSchema for configuration (recommended), OR
                  // you can copy x-hv onto AttributeNode when you build blocks from schema.

                  // OPTION A (best): ensure AttributeNode includes xHv meta copied from schemaToNode()
                  // Then filter child.attributes by child.attributes[k].xHv.systemTypes

                  const filteredChild: AttributeNode = {
                    ...child,
                    attributes: (child.attributes ?? []).filter((cfgAttr) => {
                      const xhv = cfgAttr.xHv; // typed on AttributeNode
                      return isAllowedForSystemType(xhv, systemTypeStr);
                    }),
                  };

                  return (
                    <NodeEditor
                      key={childPath}
                      node={filteredChild}
                      path={childPath}
                      value={childValue}
                      onChange={setValueForPath}
                    />
                  );
                }

                return (
                  <NodeEditor
                    key={childPath}
                    node={child}
                    path={childPath}
                    value={childValue}
                    onChange={setValueForPath}
                  />
                );
              })}
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}
